import { Dispatch, SetStateAction, useState } from 'react'
import type { DragEvent } from 'react'
import { mdiCloudUploadOutline } from '@mdi/js'
import { useIntl } from 'react-intl'

import { useAppDispatch, useAppSelector } from 'app/hooks'
import { generateThenUploadProfileZip } from './utils/generateThenUploadProfileZip'
import { currentRootDomainIdSelector } from 'features/rootDomains/selectors'
import { SpinnerCentered } from 'components/Loader'
import { userAnyTopDomainIdSelector } from 'features/auth/permissions/selectors'
import { sleep } from 'app/utils/sleep'
import { profilesPageMessages } from 'features/profiles/messages'
import { fetchProfilesListThunk } from 'features/profiles/slice'
import { profilesSelector } from 'features/profiles/selectors'
import {
  DropzoneModalBody,
  DropzoneModalDesc,
  DropzoneModalIcon,
  DropzoneModalIconWrapper,
} from 'components/Modal/DropzoneModal/styles'

interface ProfileDropZoneProps {
  handleClose: () => void
  setUploadedFilename: Dispatch<SetStateAction<string>>
  onSuccess: () => void
}

function ProfileDropZone({
  handleClose,
  setUploadedFilename,
  onSuccess,
}: ProfileDropZoneProps) {
  const dispatch = useAppDispatch()
  const { formatMessage } = useIntl()

  const [errorMessage, setErrorMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isDragActive, setIsDragActive] = useState(false)
  const rootDomainId = useAppSelector(currentRootDomainIdSelector)
  const userAnyTopDomainId = useAppSelector(userAnyTopDomainIdSelector)
  const profiles = useAppSelector(profilesSelector.data)

  const handleFilesDrop = async (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setErrorMessage('')
    setIsLoading(true)
    setIsDragActive(false)
    try {
      // TODO: webkitGetAsEntry is an experimental technology, must be removed when new Savant upload service will be implemented
      const droppedFile = event?.dataTransfer?.items[0].webkitGetAsEntry()
      setUploadedFilename(droppedFile.name.trim())
      await generateThenUploadProfileZip(
        droppedFile,
        rootDomainId,
        profiles,
        formatMessage
      )
      // backend issue - it needs ~3 sec to update profiles
      await sleep(3000)
      await dispatch(fetchProfilesListThunk(userAnyTopDomainId))
      setIsLoading(false)
      onSuccess()
      handleClose()
    } catch (err) {
      setIsLoading(false)
      console.log('File upload failed', err)
      setErrorMessage(err.message)
    }
  }

  return (
    <DropzoneModalBody
      isDragActive={isDragActive}
      isDragReject={!!errorMessage}
      onDragOver={(event) => {
        event.preventDefault()
        setIsDragActive(true)
      }}
      onDragLeave={(event) => {
        event.preventDefault()
        setIsDragActive(false)
      }}
      onDrop={(event) => handleFilesDrop(event)}
    >
      <DropzoneModalIconWrapper>
        <DropzoneModalIcon path={mdiCloudUploadOutline} />
      </DropzoneModalIconWrapper>
      <DropzoneModalDesc isDragReject={!!errorMessage}>
        {!!errorMessage
          ? errorMessage
          : formatMessage(profilesPageMessages.uploadProfileModalDesc)}
      </DropzoneModalDesc>
      {isLoading && <SpinnerCentered size="40" />}
    </DropzoneModalBody>
  )
}

export default ProfileDropZone
